import {announce} from '@github-ui/aria-live'
import type {BoardCardAddress} from '@github-ui/board-core'
import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {
  entityFieldsMutationKey,
  type ConnectionMutationVariables,
} from '@github-ui/optimistic-connection/pendingOperations'
import {registerSettlementHandler} from '@github-ui/optimistic-connection/settlementRegistry'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {type ApplyItemEdit, isBoardItemEdit} from './board-document-adapter'
import {BoardLiveUpdateProtection} from './board-live-update-protection'
import {reconcileBoardItems} from './reconcile-board-items'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardItem,
  SavedViewsBoardItemsResult,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from './types'

const BATCH_SIZE = 50
const BATCH_DELAY_MS = 100
const VERIFICATION_RETRIES = 3

// Cached cards remain usable, and later refreshes can recover without requiring a reload.
const LIVE_UPDATE_FAILED = 'Live updates are temporarily unavailable. Some cards may be out of date.'
const VERIFICATION_PENDING =
  'Some cards are waiting for confirmed changes to appear in live updates. Try reloading the board.'

type BoardLiveUpdates = {
  invalidateItem: (id: string, wait: number) => void
  onCardMount: (id: string) => () => void
  liveUpdateError: string | undefined
  onDragChange: (cards: readonly BoardCardAddress[]) => void
  onCardDialogChange: (cards: readonly BoardCardAddress[]) => void
}

type HeldItemsCache<TItem extends SavedViewsBoardItem> = {
  snapshot: SavedViewsBoardPayload<TItem, unknown> | undefined
  ids: Set<string>
}

const RefreshReason = {
  REMOUNT: 'remount',
  CHANGE: 'change',
} as const
type RefreshReason = (typeof RefreshReason)[keyof typeof RefreshReason]

export function useBoardLiveUpdates<TItem extends SavedViewsBoardItem, TMetadata = undefined>({
  adapter,
  options,
  queryKey,
  invalidatePages,
  applyItemEdit,
  projectItemForVerification,
  onItemsRefreshed,
}: {
  adapter: Pick<SavedViewsBoardAdapter<TItem, TMetadata>, 'fetchItems' | 'updateMovedItem'>
  options: SavedViewsBoardOptions
  queryKey: QueryKey
  invalidatePages: () => void
  applyItemEdit?: ApplyItemEdit<TItem>
  projectItemForVerification?: (item: TItem) => unknown
  onItemsRefreshed?: (ids: readonly string[]) => void
}): BoardLiveUpdates {
  const boardId = hashKey(queryKey)
  const queryClient = useQueryClient()
  const [error, setError] = useState<{boardId: string; message: string}>()
  const liveUpdateError = error?.boardId === boardId ? error.message : undefined
  const announcedErrorRef = useRef<string | undefined>(undefined)
  const pendingIdsRef = useRef(new Map<string, RefreshReason>())
  // Failed batches leave the queue, but their cards stay stale until a later refresh succeeds.
  const failedIdsRef = useRef(new Set<string>())
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const waitTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>())
  const activeRequestsRef = useRef(new Set<AbortController>())
  const retryTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const retriesRef = useRef(new Map<string, number>())
  const heldItemsRef = useRef<HeldItemsCache<TItem>>({snapshot: undefined, ids: new Set()})
  const mountedCards = useMemo(
    () => ({boardId, instances: new Map<string, number>(), stale: new Map<string, symbol>()}),
    [boardId],
  )
  const {protection} = useMemo(
    () => ({boardId, protection: new BoardLiveUpdateProtection<TItem>(projectItemForVerification)}),
    [boardId, projectItemForVerification],
  )
  const flushRef = useRef<() => Promise<void>>(async () => {})

  const reportError = useCallback(
    (message: string) => {
      setError({boardId, message})
      const key = JSON.stringify([boardId, message])
      if (announcedErrorRef.current !== key) {
        announcedErrorRef.current = key
        announce(message)
      }
    },
    [boardId],
  )

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current !== undefined) return
    // Collect event bursts into one request, like ../../helpers/create-batch-loader.ts.
    // Keep a fixed window so continued events cannot postpone the refresh indefinitely.
    flushTimerRef.current = setTimeout(() => void flushRef.current(), BATCH_DELAY_MS)
  }, [])

  const resumePending = useCallback(() => {
    // Ending an interaction releases queued work; it does not make the card stale on its own.
    if ([...pendingIdsRef.current.keys()].some(id => !protection.isBlocked(id))) scheduleFlush()
  }, [protection, scheduleFlush])

  const queueChanged = useCallback(
    (ids: readonly string[], reason: RefreshReason = RefreshReason.CHANGE) => {
      if (!adapter.fetchItems || ids.length === 0) return
      const current = queryClient.getQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey)
      if (heldItemsRef.current.snapshot !== current) {
        heldItemsRef.current = {
          snapshot: current,
          ids: new Set(current?.boardData.data.cells.flatMap(cell => cell.items.map(item => item.globalId))),
        }
      }
      const held = heldItemsRef.current.ids
      for (const id of ids) {
        if (!held.has(id)) continue
        if (reason === RefreshReason.CHANGE || !pendingIdsRef.current.has(id)) pendingIdsRef.current.set(id, reason)
        if (reason === RefreshReason.CHANGE) retriesRef.current.delete(id)
      }
      if (pendingIdsRef.current.size > 0) scheduleFlush()
    },
    [adapter.fetchItems, queryClient, queryKey, scheduleFlush],
  )
  const queueChangedRef = useRef(queueChanged)

  const onCardMount = useCallback(
    (id: string) => {
      const count = mountedCards.instances.get(id) ?? 0
      mountedCards.instances.set(id, count + 1)
      if (count === 0 && mountedCards.stale.has(id)) queueChangedRef.current([id], RefreshReason.REMOUNT)

      return () => {
        const remaining = (mountedCards.instances.get(id) ?? 1) - 1
        if (remaining > 0) {
          mountedCards.instances.set(id, remaining)
          return
        }
        mountedCards.instances.delete(id)
        // Effect replay and moves between mounted cells do not create a subscription gap.
        queueMicrotask(() => {
          if (!mountedCards.instances.has(id)) mountedCards.stale.set(id, Symbol())
        })
      }
    },
    [mountedCards],
  )

  const syncPendingEdits = useCallback(() => {
    const pending = new Map<number, string>()
    for (const mutation of queryClient
      .getMutationCache()
      .findAll({mutationKey: entityFieldsMutationKey(), status: 'pending'})) {
      const variables = mutation.state.variables as Partial<ConnectionMutationVariables> | undefined
      if (variables?.operation && isBoardItemEdit(variables.operation)) {
        pending.set(mutation.mutationId, variables.operation.id)
      }
    }
    return protection.setPending(pending)
  }, [protection, queryClient])

  const takeReadyIds = useCallback(() => {
    syncPendingEdits()
    return [...pendingIdsRef.current.keys()].filter(id => !protection.isBlocked(id))
  }, [protection, syncPendingEdits])

  const prepareBatch = useCallback(
    (ids: readonly string[]) => {
      // An interaction may have started while an earlier batch was being fetched.
      const ready = ids.filter(id => {
        // Skip redundant remount reads; stale markers remain available for a later retry.
        // Keep change-triggered reads even when the card is no longer mounted.
        if (
          pendingIdsRef.current.get(id) === RefreshReason.REMOUNT &&
          (!mountedCards.instances.has(id) || retryTimersRef.current.has(id))
        ) {
          pendingIdsRef.current.delete(id)
          return false
        }
        return !protection.isBlocked(id)
      })
      const remountOnly = ready.every(id => pendingIdsRef.current.get(id) === RefreshReason.REMOUNT)
      for (const id of ready) pendingIdsRef.current.delete(id)
      // Each tuple contains [card global ID, protection version at request start].
      const versions = new Map(ready.map(id => [id, protection.version(id)]))
      return {versions, remountOnly}
    },
    [mountedCards, protection],
  )

  const scheduleVerificationRetry = useCallback(
    (id: string) => {
      const retries = retriesRef.current.get(id) ?? 0
      if (retries >= VERIFICATION_RETRIES) {
        reportError(VERIFICATION_PENDING)
        return
      }
      retriesRef.current.set(id, retries + 1)
      if (retryTimersRef.current.has(id)) return
      // A confirmed write can precede search indexing. Retry after 1, 2, then 4 seconds;
      // waiting alone is not verification, so conflicting results stay protected.
      retryTimersRef.current.set(
        id,
        setTimeout(
          () => {
            retryTimersRef.current.delete(id)
            pendingIdsRef.current.set(id, RefreshReason.CHANGE)
            scheduleFlush()
          },
          1_000 * 2 ** retries,
        ),
      )
    },
    [reportError, scheduleFlush],
  )

  const verifyRefreshedItems = useCallback(
    (result: SavedViewsBoardItemsResult<TItem>, versions: ReadonlyMap<string, number>) => {
      syncPendingEdits()
      const accepted = new Set<string>()
      const refreshed = new Map(result.items.map(item => [item.item.globalId, item]))
      for (const [id, version] of versions) {
        if (protection.accept(id, version, refreshed.get(id))) {
          accepted.add(id)
          retriesRef.current.delete(id)
          const timer = retryTimersRef.current.get(id)
          if (timer !== undefined) clearTimeout(timer)
          retryTimersRef.current.delete(id)
        } else if (protection.isBlocked(id)) {
          pendingIdsRef.current.set(id, RefreshReason.CHANGE)
        } else {
          scheduleVerificationRetry(id)
        }
      }
      return accepted
    },
    [protection, scheduleVerificationRetry, syncPendingEdits],
  )

  const applyRefreshedItems = useCallback(
    (result: SavedViewsBoardItemsResult<TItem>, accepted: ReadonlySet<string>, remountOnly: boolean) => {
      if (accepted.size === 0) return
      const previous = queryClient.getQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey)
      const updated = queryClient.setQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey, current =>
        current
          ? reconcileBoardItems(current, {
              items: result.items.filter(item => accepted.has(item.item.globalId)),
              removedIds: result.removedIds.filter(id => accepted.has(id)),
            })
          : current,
      )
      // Query structural sharing preserves the snapshot when reconciliation changes nothing.
      if (!remountOnly || updated !== previous) invalidatePages()
      onItemsRefreshed?.([...accepted])
    },
    [invalidatePages, onItemsRefreshed, queryClient, queryKey],
  )

  async function flushLiveUpdates() {
    flushTimerRef.current = undefined
    if (activeRequestsRef.current.size > 0) return
    const fetchItems = adapter.fetchItems
    if (!fetchItems || pendingIdsRef.current.size === 0) return

    const controller = new AbortController()
    activeRequestsRef.current.add(controller)
    let batchIds: readonly string[] = []
    try {
      while (pendingIdsRef.current.size > 0 && !controller.signal.aborted) {
        const ids = takeReadyIds()
        if (ids.length === 0) break
        for (let index = 0; index < ids.length; index += BATCH_SIZE) {
          const {versions, remountOnly} = prepareBatch(ids.slice(index, index + BATCH_SIZE))
          if (versions.size === 0) continue
          batchIds = [...versions.keys()]
          // A read started offscreen cannot cover updates missed before the card resubscribes.
          const refreshGenerations = new Map(
            batchIds.filter(id => mountedCards.instances.has(id)).map(id => [id, mountedCards.stale.get(id)]),
          )
          // Refresh this batch of card IDs in one request, not one request per card.
          // Await each batch before starting the next.
          const result = await fetchItems({
            ...options,
            ids: batchIds,
            signal: controller.signal,
          })
          if (controller.signal.aborted) break
          const accepted = verifyRefreshedItems(result, versions)
          for (const id of accepted) {
            if (
              pendingIdsRef.current.get(id) === RefreshReason.REMOUNT &&
              (!refreshGenerations.has(id) || mountedCards.stale.get(id) !== refreshGenerations.get(id))
            ) {
              // An older result may remove the card; keep its newer-gap recovery queued even offscreen.
              pendingIdsRef.current.set(id, RefreshReason.CHANGE)
            }
          }
          applyRefreshedItems(result, accepted, remountOnly)
          for (const id of accepted) {
            failedIdsRef.current.delete(id)
            // An earlier read cannot cover a newer gap, or updates missed while still offscreen.
            if (mountedCards.instances.has(id) && mountedCards.stale.get(id) === refreshGenerations.get(id)) {
              mountedCards.stale.delete(id)
            }
          }
        }
      }
      if (
        !controller.signal.aborted &&
        pendingIdsRef.current.size === 0 &&
        retriesRef.current.size === 0 &&
        failedIdsRef.current.size === 0
      ) {
        setError(undefined)
        announcedErrorRef.current = undefined
      }
    } catch {
      if (!controller.signal.aborted) {
        for (const id of batchIds) failedIdsRef.current.add(id)
        reportError(LIVE_UPDATE_FAILED)
      }
    } finally {
      activeRequestsRef.current.delete(controller)
      if (!controller.signal.aborted) resumePending()
    }
  }

  useLayoutEffect(() => {
    flushRef.current = flushLiveUpdates
    queueChangedRef.current = queueChanged
  })

  const invalidateItem = useCallback(
    (id: string, wait: number) => {
      // Alive's delay lets replicated reads catch up before the item is queued.
      // Like ../../hooks/use-issues-dashboard-live-updates.tsx, track replication-delay
      // timers so they can be cancelled when leaving the board.
      scheduleReplicationWait(
        waitTimersRef.current,
        () => {
          pendingIdsRef.current.set(id, RefreshReason.CHANGE)
          retriesRef.current.delete(id)
          scheduleFlush()
        },
        wait,
      )
    },
    [scheduleFlush],
  )

  const onDragChange = useCallback(
    (cards: readonly BoardCardAddress[]) => {
      protection.setInteraction('drag', cards)
      resumePending()
    },
    [protection, resumePending],
  )
  const onCardDialogChange = useCallback(
    (cards: readonly BoardCardAddress[]) => {
      protection.setInteraction('dialog', cards)
      resumePending()
    },
    [protection, resumePending],
  )

  const recordConfirmation = useEffectEvent((operation: ConnectionOperation) => {
    if (!isBoardItemEdit(operation)) return
    const current = queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey)
    if (current && protection.confirm(operation, current, applyItemEdit, adapter.updateMovedItem)) {
      queueChanged([operation.id])
    }
  })

  useEffect(() => {
    if (!adapter.fetchItems) return
    const unsubscribe = queryClient.getMutationCache().subscribe(() => queueChanged(syncPendingEdits()))
    queueChanged(syncPendingEdits())
    const unregister = registerSettlementHandler(async operation => recordConfirmation(operation))

    return () => {
      unsubscribe()
      unregister()
    }
  }, [adapter.fetchItems, queryClient, syncPendingEdits, queueChanged])

  useEffect(() => {
    const waitTimers = waitTimersRef.current
    const requests = activeRequestsRef.current
    const pendingIds = pendingIdsRef.current
    const failedIds = failedIdsRef.current
    const retryTimers = retryTimersRef.current
    const retries = retriesRef.current

    return () => {
      if (flushTimerRef.current !== undefined) clearTimeout(flushTimerRef.current)
      flushTimerRef.current = undefined
      for (const timer of waitTimers) clearTimeout(timer)
      waitTimers.clear()
      for (const request of requests) request.abort()
      requests.clear()
      pendingIds.clear()
      failedIds.clear()
      for (const timer of retryTimers.values()) clearTimeout(timer)
      retryTimers.clear()
      retries.clear()
      setError(undefined)
      announcedErrorRef.current = undefined
    }
  }, [boardId])

  return {invalidateItem, onCardMount, liveUpdateError, onDragChange, onCardDialogChange}
}

function scheduleReplicationWait(timers: Set<ReturnType<typeof setTimeout>>, onReady: () => void, wait: number) {
  const timer = setTimeout(() => {
    timers.delete(timer)
    onReady()
  }, wait)
  timers.add(timer)
}
